import { IDeviceConfiguration } from "@formant/core/model/IDeviceConfiguration";
import { IDeviceTeleopCustomStreamConfiguration } from "@formant/core/model/IDeviceTeleopCustomStreamConfiguration";
import { IDeviceTeleopHardwareStreamConfiguration } from "@formant/core/model/IDeviceTeleopHardwareStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { IRtcCapabilitySet } from "@formant/core/model/IRtcCapabilitySet";
import { IRtcStreamCapability } from "@formant/core/model/IRtcStreamCapability";
import {
    rtcBooleanStreamType,
    rtcJointStateStreamType,
    rtcNumericStreamType,
    rtcPoseStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { Uuid } from "@formant/core/model/Uuid";

import { teleopRosTopicTypeToRtcStreamType } from "./teleopRosTopicTypeToRtcStreamType";

function rosStreamToRtcStreamCapabilities(
    deviceId: Uuid,
    rosStream: IDeviceTeleopRosStreamConfiguration,
): IRtcStreamCapability[] {
    const capabilities = [
        {
            stream: {
                entityId: deviceId,
                streamName: rosStream.topicName,
                streamType: teleopRosTopicTypeToRtcStreamType(
                    rosStream.topicType,
                    undefined,
                ),
            },
            send: rosStream.mode === "observe",
            receive: rosStream.mode === "command",
        },
        {
            stream: {
                entityId: deviceId,
                streamName: rosStream.statusTopic,
                streamType: rtcBooleanStreamType,
            },
            send: true,
            receive: false,
        },
        {
            stream: {
                entityId: deviceId,
                streamName: rosStream.plannedTopic,
                streamType: rtcJointStateStreamType,
            },
            send: true,
            receive: false,
        },
        {
            stream: {
                entityId: deviceId,
                streamName: rosStream.endEffectorTopic,
                streamType: rtcPoseStreamType,
            },
            send: false,
            receive: true,
        },
        {
            stream: {
                entityId: deviceId,
                streamName: rosStream.planValidTopic,
                streamType: rtcBooleanStreamType,
            },
            send: true,
            receive: false,
        },
        {
            stream: {
                entityId: deviceId,
                streamName: rosStream.valueTopicName,
                streamType: rtcNumericStreamType,
            },
            send: true,
            receive: false,
        },
    ];
    return capabilities.filter(
        (_): _ is IRtcStreamCapability => _.stream.streamName !== undefined,
    );
}

function customStreamToRtcStreamCapability(
    deviceId: Uuid,
    customStream: IDeviceTeleopCustomStreamConfiguration,
): IRtcStreamCapability[] {
    const capabilities = [
        {
            stream: {
                entityId: deviceId,
                streamName: customStream.name,
                streamType: customStream.rtcStreamType,
            },
            send: customStream.mode === "observe",
            receive: customStream.mode === "command",
        },
        {
            stream: {
                entityId: deviceId,
                streamName: customStream.valueStreamName,
                streamType: rtcNumericStreamType,
            },
            send: true,
            receive: false,
        },
    ];
    return capabilities.filter(
        (_): _ is IRtcStreamCapability => _.stream.streamName !== undefined,
    );
}

function hardwareStreamToRtcStreamCapability(
    deviceId: Uuid,
    hardwareStream: IDeviceTeleopHardwareStreamConfiguration,
): IRtcStreamCapability {
    return {
        stream: {
            entityId: deviceId,
            streamName: hardwareStream.name,
            streamType: hardwareStream.rtcStreamType,
        },
        send: hardwareStream.mode === "observe",
        receive: hardwareStream.mode === "command",
    };
}

// Stream definitions live in two configuration sections that are edited by
// separate UI surfaces: `document.teleop` (the original section, which also
// holds teleop UI layout such as joysticks and views) and `document.realtime`
// (the newer, layout-independent section). A stream configured in only one
// section must still be negotiated, otherwise it silently fails to stream.
// Streams are merged by their unique identifier, with the teleop section taking
// precedence so behavior is unchanged for streams that already stream today.
function mergeStreamsByKey<T>(
    primary: T[] | undefined,
    secondary: T[] | undefined,
    getKey: (stream: T) => string,
): T[] {
    const merged = new Map<string, T>();

    for (const stream of primary ?? []) {
        merged.set(getKey(stream), stream);
    }
    // Secondary-section streams are only added when the primary section does not
    // already define that key, so the teleop section keeps precedence.
    for (const stream of secondary ?? []) {
        const key = getKey(stream);
        if (!merged.has(key)) {
            merged.set(key, stream);
        }
    }

    return [...merged.values()];
}

export function getRtcCapabilitySet(
    deviceConfiguration: IDeviceConfiguration,
): IRtcCapabilitySet | undefined {
    const { deviceId } = deviceConfiguration;
    if (!deviceId) {
        return;
    }

    const { teleop, realtime, portForwarding } = deviceConfiguration.document;

    const rosStreams = mergeStreamsByKey(
        teleop?.rosStreams,
        realtime?.rosStreams,
        stream => stream.topicName,
    );
    const customStreams = mergeStreamsByKey(
        teleop?.customStreams,
        realtime?.customStreams,
        stream => stream.name,
    );
    const hardwareStreams = mergeStreamsByKey(
        teleop?.hardwareStreams,
        realtime?.hardwareStreams,
        stream => stream.name,
    );

    const streams = [
        ...(rosStreams
            ? rosStreams.reduce<IRtcStreamCapability[]>((acc, _) => {
                  return [
                      ...acc,
                      ...rosStreamToRtcStreamCapabilities(deviceId, _),
                  ];
              }, [])
            : []),
        ...(customStreams
            ? customStreams.reduce<IRtcStreamCapability[]>((acc, _) => {
                  return [
                      ...acc,
                      ...customStreamToRtcStreamCapability(deviceId, _),
                  ];
              }, [])
            : []),
        ...(hardwareStreams
            ? hardwareStreams.map(_ =>
                  hardwareStreamToRtcStreamCapability(deviceId, _),
              )
            : []),
    ];

    return {
        streaming: streams.length > 0 ? { streams } : undefined,
        portForwarding: portForwarding ? {} : undefined,
    };
}
